import { excelDownloadId, pdfDownloadId, searchId } from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import { FC, ReactElement, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "query-string";
import {
  Button,
  DataHeader,
  Dropdown,
  Flex,
  Input,
  InputNumber,
  LegacyDatePicker,
  LegacyForm,
  Select,
  Typography,
  usePaypingContext,
} from "@payping/web-components";
import { SDKDateObject, dateFormats, jalaliNow, toJalali, toUtc } from "@payping/utils/dateUtils";
import { CuToman, PhRDownloadSimple } from "@payping/icons";
import { useAsync } from "react-use";
import { getClients } from "@payping/redux/actions";
import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { IpgTypeEnum, PaymentStatusEnum, sdkFetch, uploadV2Apis } from "@payping/api";
// the v2 export endpoints still work with the legacy report filters (Default/IsPaid/UnPaid)
import { PaymentStatusEnum as LegacyPaymentStatusEnum } from "@payping/api/app/api/reportTypes";
import { transactionFiltersSearchVariant } from "@/pages/Sale/Report/ShowAllTransactionDetails";
import { numberFormatter } from "@payping/utilities-shared/number";
import { NumberListParamsType, StringListParamsType } from "@/pages/Sale/Report/types";
import { DownloadTypes } from "./AppSubNav/Download";
import { submitQueryParams } from "@payping/utils/query-string";

interface Props {
  retry: () => void;
  loading: boolean;
}

interface FormInputs {
  dateRange?: [SDKDateObject | undefined, SDKDateObject | undefined];
  amount?: number | null;
  ipgTypes?: IpgTypeEnum[] | null;
  paymentStatuses?: PaymentStatusEnum[] | null;
  clientId?: string | null;
  clientRefId?: string | null;
  codes?: string[] | null;
  refId?: string | null;
  ipgRefId?: string | null;
  cardNo?: string | null;
  payerName?: string | null;
  payerIdentity?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  searchVariant?: (typeof transactionFiltersSearchVariant)[number] | null;
}

const PAID_PAYMENT_STATUSES: PaymentStatusEnum[] = [
  PaymentStatusEnum.PendingForSettlement,
  PaymentStatusEnum.Succeeded,
];

// v2 Excel/PDF export endpoints still expect the legacy single status (Default/IsPaid/UnPaid);
// a mixed selection can't be expressed, so it falls back to no status filter
const toLegacyPaymentStatus = (statuses?: PaymentStatusEnum[] | null): LegacyPaymentStatusEnum | undefined => {
  if (!statuses?.length) {
    return undefined;
  }
  if (statuses.every((status) => PAID_PAYMENT_STATUSES.includes(status))) {
    return LegacyPaymentStatusEnum.IsPaid;
  }
  if (statuses.every((status) => !PAID_PAYMENT_STATUSES.includes(status))) {
    return LegacyPaymentStatusEnum.UnPaid;
  }
  return undefined;
};

/**
 * TransactionFilter component.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.loading Whether the component is loading.
 * @returns {ReactElement} The TransactionFilter component.
 */
export const TransactionFilter: FC<Props> = (props) => {
  const { retry, loading } = props;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = LegacyForm.useForm<FormInputs>();
  const parsedSearch = qs.parse(location.search, { arrayFormat: "bracket" });
  const { paymentStatuses, amount, ipgTypes }: Partial<NumberListParamsType> = qs.parse(location.search, {
    parseNumbers: true,
    arrayFormat: "bracket",
  });
  const {
    cardNo,
    payerName,
    payerIdentity,
    clientId,
    refId,
    ipgRefId,
    clientRefId,
    toDate,
    fromDate,
    codes,
    searchVariant,
  }: Partial<StringListParamsType> = qs.parse(location.search, {
    arrayFormat: "bracket",
  });
  const [openDrawer, setOpenDrawer] = useState(false);
  const { breakpoints } = usePaypingContext();
  const { xl } = breakpoints;
  const dispatch = useAppDispatch();

  const { loading: isLoadingClients, value: clients } = useAsync(() => dispatch(getClients()), [dispatch]);

  useEffect(() => {
    const fromDateRange =
      (fromDate ? toJalali({ date: fromDate }).object : jalaliNow().object.add(-1, "months")) ?? null;
    const toDateRange = (toDate ? toJalali({ date: toDate }).object : jalaliNow().object) ?? null;
    form.setFieldsValue({
      clientId: clientId ?? undefined,
      amount: amount ?? undefined,
      ipgTypes: ipgTypes ?? undefined,
      paymentStatuses: paymentStatuses ?? [PaymentStatusEnum.Succeeded],
      clientRefId: clientRefId ?? undefined,
      codes: codes ?? undefined,
      ipgRefId: ipgRefId ?? undefined,
      refId: refId ?? undefined,
      cardNo: cardNo ?? undefined,
      payerName: payerName ?? undefined,
      payerIdentity: payerIdentity ?? undefined,
      dateRange: [fromDateRange ?? undefined, toDateRange ?? undefined],
      searchVariant: searchVariant ?? "searchByType",
    });
  }, [
    amount,
    cardNo,
    clientId,
    clientRefId,
    codes,
    form,
    fromDate,
    ipgRefId,
    ipgTypes,
    payerIdentity,
    payerName,
    paymentStatuses,
    refId,
    searchVariant,
    toDate,
  ]);

  const onReset = (): void => {
    // new
    delete parsedSearch.fromDate;
    delete parsedSearch.toDate;
    delete parsedSearch.clientId;
    delete parsedSearch.refId;
    delete parsedSearch.ipgRefId;
    delete parsedSearch.clientRefId;
    delete parsedSearch.amount;
    delete parsedSearch.paymentStatuses;
    delete parsedSearch.ipgTypes;
    delete parsedSearch.codes;
    delete parsedSearch.payerName;
    delete parsedSearch.payerIdentity;
    delete parsedSearch.cardNo;
    delete parsedSearch.searchVariant;
    navigate({ pathname: location.pathname, search: qs.stringify(parsedSearch) });
    form.resetFields();
    setOpenDrawer(false);
  };

  const onFinish = (values: FormInputs) => {
    const date = values.dateRange || [undefined, undefined];
    const newValues = {
      fromDate: toUtc({ date: date[0], toFormat: dateFormats.standard.utcDateTime }).string,
      toDate: toUtc({ date: date[1], toFormat: dateFormats.standard.utcDateTime }).string,

      clientId: values.clientId,
      refId: values.refId,
      ipgRefId: values.ipgRefId,
      clientRefId: values.clientRefId,
      minAmount: values.amount,
      maxAmount: values.amount,
      paymentStatuses: values.paymentStatuses && values.paymentStatuses.length > 0 ? values.paymentStatuses : null,
      ipgTypes: values.ipgTypes,
      codes: values.codes,
      payerName: values.payerName,
      payerIdentity: values.payerIdentity,
      cardNo: values.cardNo,
      searchVariant: values.searchVariant,
    };
    submitQueryParams({
      navigate,
      newValues,
      parsedSearch,
      pathname: location.pathname,
      retry,
      stringifyOptions: { arrayFormat: "bracket" },
    });
    setOpenDrawer(false);
  };

  const paymentStatusOptions = [
    { label: t("report.filters.paymentStatus.ReadyForProcess"), value: PaymentStatusEnum.ReadyForProcess },
    {
      label: t("report.filters.paymentStatus.SuspiciousVerification"),
      value: PaymentStatusEnum.SuspiciousVerification,
    },
    { label: t("report.filters.paymentStatus.Failed"), value: PaymentStatusEnum.Failed },
    { label: t("report.filters.paymentStatus.PendingForSettlement"), value: PaymentStatusEnum.PendingForSettlement },
    { label: t("report.filters.paymentStatus.SuspiciousReverse"), value: PaymentStatusEnum.SuspiciousReverse },
    { label: t("report.filters.paymentStatus.Succeeded"), value: PaymentStatusEnum.Succeeded },
    { label: t("report.filters.paymentStatus.Reversed"), value: PaymentStatusEnum.Reversed },
  ];

  const handleDownload = useCallback(
    async (type: DownloadTypes, newWindow: Window | null): Promise<void> => {
      const body = {
        amount: amount ?? undefined,
        cardNumber: cardNo ?? undefined,
        clientId: clientId ?? undefined,
        clientRefId: clientRefId,
        code: codes && codes.length > 0 ? codes[0] : undefined,
        fromDate: fromDate ?? undefined,
        ipgRefId: ipgRefId ?? undefined,
        payerName: payerName,
        payerPhoneNumber: payerIdentity ?? undefined,
        paymentStatus: toLegacyPaymentStatus(paymentStatuses),
        paymentType: ipgTypes?.length === 1 ? ipgTypes[0] : undefined,
        refId: refId ?? undefined,
        toDate: toDate ?? undefined,
      };
      let url;
      if (type === "Excel") {
        const response = await sdkFetch("/v2/upload/PaymentsExcelExport", {
          body: JSON.stringify(body),
          method: "POST",
        });
        url = await response.text();
      } else {
        // @ts-expect-error paymentType
        url = await uploadV2Apis.paymentsPdfExportWithModelPost(body);
      }
      newWindow?.location?.assign?.(url);
    },
    [
      amount,
      cardNo,
      clientId,
      clientRefId,
      codes,
      fromDate,
      ipgRefId,
      payerName,
      payerIdentity,
      paymentStatuses,
      ipgTypes,
      refId,
      toDate,
    ],
  );

  const items = [
    {
      key: "1",
      label: <span data-google-track={excelDownloadId}>{t("downloadExcel")}</span>,
      onClick: () => {
        const newWindow = window.open(undefined, "_blank");
        handleDownload("Excel", newWindow);
      },
    },
    {
      key: "2",
      label: <span data-google-track={pdfDownloadId}>{t("downloadPdf")}</span>,
      onClick: () => {
        const newWindow = window.open(undefined, "_blank");
        handleDownload("PDF", newWindow);
      },
    },
  ];

  const searchByOptions = (
    <>
      <Select.Option disabled={true} value={"searchByType"}>
        {t("report.searchBy")}
      </Select.Option>
      <Select.Option value={"payerName"}>{t("report.filters.payerName")}</Select.Option>
      <Select.Option value={"payerIdentity"}>{t("report.filters.payerIdentity")}</Select.Option>
      <Select.Option value={"code"}>{t("report.filters.code")}</Select.Option>
      <Select.Option value={"refId"}>{t("report.filters.refId")}</Select.Option>
      <Select.Option value={"clientRefId"}>{t("report.filters.clientRefId")}</Select.Option>
      <Select.Option value={"ipgRefId"}>{t("report.filters.ipgRefId")}</Select.Option>
      <Select.Option value={"cardNo"}>{t("report.filters.cardNumber")}</Select.Option>
    </>
  );

  return (
    <LegacyForm<FormInputs>
      onFinish={onFinish}
      form={form}
      onValuesChange={async (changedValues) => {
        if ("searchVariant" in changedValues) {
          form.resetFields(["payerName", "payerIdentity", "codes", "refId", "clientRefId", "ipgRefId", "cardNo"]);
        }
      }}
    >
      <DataHeader
        title={
          <Typography.Title className={"tw:m-0!"} level={4}>
            {t("mySale.tabs.report.transactions.recent")}
          </Typography.Title>
        }
        filters={{
          searchbarInput: xl ? (
            <>
              <LegacyForm.Item
                noStyle
                shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
                key={1}
              >
                {(form1): ReactElement => (
                  <LegacyForm.Item name={form1.getFieldValue("searchVariant")} noStyle>
                    <Input.Search
                      disabled={form1.getFieldValue("searchVariant") === "searchByType"}
                      allowClear
                      onSearch={() => form.submit()}
                      addonBefore={
                        <LegacyForm.Item name={"searchVariant"} noStyle>
                          <Select popupMatchSelectWidth={false}>{searchByOptions}</Select>
                        </LegacyForm.Item>
                      }
                      placeholder={t("report.search")}
                      data-google-track={searchId}
                    />
                  </LegacyForm.Item>
                )}
              </LegacyForm.Item>
            </>
          ) : null,
          datepickerInput: (
            <LegacyForm.Item className={"tw:m-0!"} name="dateRange">
              <LegacyDatePicker.RangePicker allowClear={false} onChange={() => form.submit()} className={"w-100"} />
            </LegacyForm.Item>
          ),
          filtersButton: (
            <>
              {/* < Filters Button & Badge > */}
              <FiltersButton
                onClick={() => setOpenDrawer(true)}
                params={{
                  amount: amount ?? undefined,
                  clientId: clientId ?? undefined,
                  paymentStatuses: paymentStatuses !== undefined,
                  ipgTypes: ipgTypes ?? undefined,
                }}
              />
              {/* </ Filters Button & Badge > */}

              {/* < FiltersDrawer > */}
              <FiltersDrawer
                title={t("filter")}
                onClose={() => setOpenDrawer(false)}
                open={openDrawer}
                loading={loading}
                onClickApplyFilters={() => {
                  form.submit();
                }}
                // eslint-disable-next-line react-hooks/immutability
                onClickResetFilters={() => {
                  onReset();
                }}
              >
                <Flex vertical gap={8}>
                  {/* < Amount > */}
                  <LegacyForm.Item label={t("report.filters.amount")} name="amount" layout={"vertical"}>
                    <InputNumber
                      formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                      parser={(value) => numberFormatter.parse(value)}
                      className="tw:w-full!"
                      suffix={<CuToman width={24} height={24} />}
                      inputMode="numeric"
                      changeOnWheel={false}
                      controls={false}
                    />
                  </LegacyForm.Item>
                  {/* </ Amount /> */}

                  {!xl ? (
                    <>
                      {/* < SearchVariant > */}
                      <LegacyForm.Item
                        label={t("report.search")}
                        shouldUpdate={(prev, curr): boolean => prev.searchVariant !== curr.searchVariant}
                        key={1}
                        layout={"vertical"}
                      >
                        {(form1): ReactElement => (
                          <LegacyForm.Item name={form1.getFieldValue("searchVariant")} noStyle>
                            <Input
                              disabled={form1.getFieldValue("searchVariant") === "searchByType"}
                              allowClear
                              addonBefore={
                                <LegacyForm.Item name={"searchVariant"} noStyle>
                                  <Select popupMatchSelectWidth={false}>{searchByOptions}</Select>
                                </LegacyForm.Item>
                              }
                              placeholder={t("report.search")}
                              data-google-track={searchId}
                            />
                          </LegacyForm.Item>
                        )}
                      </LegacyForm.Item>
                      {/* </ SearchVariant > */}
                    </>
                  ) : null}

                  {/* < Noe Service > */}
                  <LegacyForm.Item label={t("report.filters.clientId")} name="clientId" layout={"vertical"}>
                    <Select allowClear disabled={isLoadingClients}>
                      {clients?.map(({ displayName, clientId: cId }) => (
                        <Select.Option key={cId} value={cId}>
                          {displayName}
                        </Select.Option>
                      ))}
                    </Select>
                  </LegacyForm.Item>
                  {/* </ Noe Service > */}

                  {/* < Noe Dargah > */}
                  <LegacyForm.Item label={t("report.filters.paymentType")} name="ipgTypes" layout={"vertical"}>
                    <Select
                      mode="multiple"
                      allowClear
                      options={[
                        { label: t("ipg.type.BehPardakht"), value: IpgTypeEnum.Mellat },
                        { label: t("ipg.type.Saman"), value: IpgTypeEnum.Sep },
                        { label: t("ipg.type.IranKish"), value: IpgTypeEnum.IranKish },
                        { label: t("ipg.type.Mabna"), value: IpgTypeEnum.Mabna },
                        { label: t("ipg.type.Pasargad"), value: IpgTypeEnum.Pep },
                        { label: t("ipg.type.Sadad"), value: IpgTypeEnum.Sadad },
                        { label: t("ipg.type.Parsian"), value: IpgTypeEnum.Pec },
                      ]}
                    />
                  </LegacyForm.Item>
                  {/* </ Noe Dargah > */}

                  {/* < Vaziat Tarakonesh > */}
                  <LegacyForm.Item
                    label={t("report.filters.paymentStatus.title")}
                    name="paymentStatuses"
                    layout={"vertical"}
                  >
                    <Select mode="multiple" allowClear options={paymentStatusOptions} />
                  </LegacyForm.Item>
                  {/* </ Vaziat Tarakonesh > */}
                </Flex>
              </FiltersDrawer>
              {/* </ FiltersDrawer > */}
            </>
          ),
          downloadButton: (
            <LegacyForm.Item noStyle key={3}>
              <Dropdown
                menu={{
                  items,
                }}
              >
                <Button block icon={<PhRDownloadSimple width={14} height={14} />}>
                  {t("downloadFile")}
                </Button>
              </Dropdown>
            </LegacyForm.Item>
          ),
        }}
      />
    </LegacyForm>
  );
};
